import { useEffect, useRef, useState } from 'react';
import type { VideoRequest } from '../services/local-video';
import { useCinematicWaypoints } from '../studio/use-cinematic-waypoints';
import { type StudioPlaybackMode } from '../studio/use-studio-playback';
import { openingPreviewBounds, previewClockSeconds } from '../studio/preview-timing';
import { resolveExportTiming, type ExportRange } from '../../../renderer/src/export-range';
import { frameProgress } from '../../../renderer/src/timeline';
import type { Ride } from '../domain/ride';
import type { DraftControls } from '../studio/StudioSettings';
import { enforceFootageRange } from './timeline/footage-range';
import type { PlaybackScope } from './timeline/TimelineTransport';

/** Uses the Studio's full-preview, selected-range, opening, and pause/resume rules. */
export function useStudioTimelinePlayback(ride: Ride, controls: DraftControls, request: VideoRequest, restoreRange = controls.update) {
  const { draft, update } = controls;
  const [playing, setPlaying] = useState(false);
  const [playbackMode, setPlaybackMode] = useState<StudioPlaybackMode>('range');
  const [scope, setScope] = useState<PlaybackScope>('range');
  const [rangeInvalid, setRangeInvalid] = useState(false);
  const resumeMode = useRef<StudioPlaybackMode | null>(null);
  const playbackEnd = useRef(1);
  const linkedPreviewStarted = useRef(false);
  const timing = resolveExportTiming(request);
  const originalRange = { inFrame: timing.inFrame, outFrame: timing.outFrame };
  const range = enforceFootageRange(originalRange, timing.filmFrames - 1);
  useEffect(() => {
    if (range.inFrame !== originalRange.inFrame || range.outFrame !== originalRange.outFrame)
      restoreRange(current => ({ ...current, exportRange: { ...current.exportRange, ...range } }));
  }, [range.inFrame, range.outFrame, originalRange.inFrame, originalRange.outFrame, restoreRange]);
  const setPosition = (progress: number, persist = true) => {
    resumeMode.current = null; setPlaying(false);
    if (progress !== draft.progress) update(current => ({ ...current, progress }), persist);
  };
  const editing = useCinematicWaypoints(ride, controls, request.durationSeconds, setPosition, playing, request.presentation);
  const seek = (progress: number, persist = true) => { editing.follow(); setPosition(progress, persist); };
  const sequenceRequest = editing.previewSequence ? { ...request, cinematicSequence: editing.previewSequence } : request;
  const previewRequest = playbackMode === 'full' ? { ...sequenceRequest, exportRange: undefined } : sequenceRequest;
  const previewTiming = resolveExportTiming(previewRequest);
  const previewStart = frameProgress(previewTiming.inFrame, previewTiming.filmFrames);
  const previewEnd = frameProgress(previewTiming.outFrame, previewTiming.filmFrames);
  const filmProgress = Math.max(0, Math.min(1, (draft.progress - previewStart) / (previewEnd - previewStart)));
  const play = (mode: 'full' | 'range') => {
    if (playing && playbackMode === mode) { resumeMode.current = mode; setPlaying(false); return; }
    editing.follow();
    const start = mode === 'full' ? 0 : frameProgress(range.inFrame, timing.filmFrames);
    const end = mode === 'full' ? 1 : frameProgress(range.outFrame, timing.filmFrames);
    if (draft.progress < start || draft.progress >= end) seek(start, false);
    resumeMode.current = null; setPlaybackMode(mode); playbackEnd.current = end; setPlaying(true);
  };
  const togglePlay = () => {
    if (playing) { resumeMode.current = playbackMode; setPlaying(false); return; }
    editing.follow();
    const bounds = playbackMode === 'opening' ? openingPreviewBounds(timing) : { start: previewStart, end: previewEnd };
    if (draft.progress < bounds.start || draft.progress >= bounds.end) seek(bounds.start);
    resumeMode.current = null; playbackEnd.current = bounds.end; setPlaying(true);
  };
  const changeRange = (exportRange: ExportRange | null, persist = true) => {
    resumeMode.current = null; setPlaying(false); setPlaybackMode(scope);
    update(current => {
      const next = exportRange && { ...current.exportRange, ...enforceFootageRange(exportRange, timing.filmFrames - 1) };
      return JSON.stringify(next) === JSON.stringify(current.exportRange) ? current : { ...current, exportRange: next };
    }, persist);
  };
  const changeScope = (value: PlaybackScope) => { setPlaying(false); setScope(value); setPlaybackMode(value); resumeMode.current = null; };
  const toggleScopePlay = () => play(scope);
  const restart = () => seek(scope === 'full' ? 0 : frameProgress(range.inFrame, timing.filmFrames), false);
  const replayOpening = () => {
    if (playing && playbackMode === 'opening') { setPlaying(false); return; }
    const opening = openingPreviewBounds(timing); seek(opening.start); setPlaybackMode('opening');
    playbackEnd.current = opening.end; setPlaying(true);
  };
  const clock = { seconds: previewClockSeconds(previewTiming), end: playbackEnd.current };
  useEffect(() => { setPlaying(false); }, [request.durationSeconds]);
  useEffect(() => {
    if (linkedPreviewStarted.current || !ride.sourceFingerprint?.startsWith('studio-project:')) return;
    const seconds = Number(new URLSearchParams(location.search).get('previewSeconds'));
    if (!Number.isFinite(seconds) || seconds < .1 || seconds > 30) return;
    linkedPreviewStarted.current = true;
    setPlaybackMode('full'); playbackEnd.current = Math.min(1, draft.progress + seconds / request.durationSeconds); setPlaying(true);
  }, [request.durationSeconds, ride.sourceFingerprint]);
  useEffect(() => {
    if (!playing && playbackMode === 'opening' && draft.progress >= openingPreviewBounds(timing).end) setPlaybackMode(scope);
  }, [playing, draft.progress, playbackMode, scope]);
  return { playing, setPlaying, playbackMode, scope, changeScope, toggleScopePlay, restart, rangeInvalid, setRangeInvalid, editing, seek, play, togglePlay, changeRange,
    replayOpening, previewRequest, previewTiming, previewStart, filmProgress, range, clock };
}
